import { useCallback } from 'react'
import {
  applySelectionLockPolicy,
  areIdArraysEqual,
  normalizeSelectableIdsForScope,
} from '../helpers/selection-scope'
import type { ShapeManagerStateApi } from './use-shape-manager-state'
import type { SelectionHistoryApi } from './use-selection-history'
import type { EditScopeRefsApi } from './use-edit-scope-refs'

export function useShapeLockFreeze(
  ctx: ShapeManagerStateApi & SelectionHistoryApi & EditScopeRefsApi
) {
  const {
    editScopeGroupIdRef,
    onShapeCommitted,
    onUserEdit,
    recordShapeSelectionHistory,
    selectedShapeIdsRef,
    setSelectedShapeIds,
    setShapes,
    shapesRef,
  } = ctx

  const commitLockStateChanges = useCallback(
    (
      ids: string[],
      locked: boolean,
      historyPrefix: 'lock' | 'unlock' | 'unlock-all',
      historyTxnIdOverride?: string
    ): string[] => {
      const beforeList = shapesRef.current
      const beforeById = new Map(beforeList.map((shape) => [shape.id, shape]))
      const idsToChange = ids.filter((id) => {
        const shape = beforeById.get(id)

        if (!shape) return false
        if (shape.locked === locked) return false
        // Mutual exclusivity: a frozen shape cannot be locked.
        if (locked && shape.frozen === true) return false
        return true
      })

      if (idsToChange.length === 0) return []

      const idSet = new Set(idsToChange)
      const historyTxnId =
        historyTxnIdOverride ?? `${historyPrefix}:${idsToChange.slice().sort().join('|')}`
      const nextShapes = beforeList.map((shape) =>
        idSet.has(shape.id) ? { ...shape, locked } : shape
      )

      shapesRef.current = nextShapes
      setShapes(nextShapes)
      const beforeSelection = [...selectedShapeIdsRef.current]
      const nextSelectionBase = normalizeSelectableIdsForScope(
        beforeSelection.filter((id) => nextShapes.some((shape) => shape.id === id)),
        nextShapes,
        editScopeGroupIdRef.current
      )
      const nextSelection = locked
        ? nextSelectionBase
        : applySelectionLockPolicy(nextSelectionBase, nextShapes)

      if (!areIdArraysEqual(beforeSelection, nextSelection)) {
        selectedShapeIdsRef.current = nextSelection
        setSelectedShapeIds(nextSelection)
        recordShapeSelectionHistory(beforeSelection, nextSelection, {
          source: 'command',
          gesture: 'replace',
          historyTxnId,
        })
      }

      onUserEdit?.(idsToChange)

      const nextById = new Map(nextShapes.map((shape) => [shape.id, shape]))

      for (const id of idsToChange) {
        const before = beforeById.get(id)
        const after = nextById.get(id)

        if (!before || !after) continue
        onShapeCommitted?.(after, 'update', {
          before: { locked: before.locked === true },
          after: { locked },
          historyTxnId,
        })
      }

      return idsToChange
    },
    [
      onShapeCommitted,
      onUserEdit,
      recordShapeSelectionHistory,
      editScopeGroupIdRef,
      selectedShapeIdsRef,
      setSelectedShapeIds,
      setShapes,
      shapesRef,
    ]
  )

  const lockSelected = useCallback(
    (opts?: { historyTxnId?: string }): string[] => {
      const ids = normalizeSelectableIdsForScope(
        selectedShapeIdsRef.current,
        shapesRef.current,
        editScopeGroupIdRef.current
      )

      return commitLockStateChanges(ids, true, 'lock', opts?.historyTxnId)
    },
    [commitLockStateChanges, editScopeGroupIdRef, selectedShapeIdsRef, shapesRef]
  )

  const unlockSelected = useCallback(
    (opts?: { historyTxnId?: string }): string[] => {
      const ids = normalizeSelectableIdsForScope(
        selectedShapeIdsRef.current,
        shapesRef.current,
        editScopeGroupIdRef.current
      )

      return commitLockStateChanges(ids, false, 'unlock', opts?.historyTxnId)
    },
    [commitLockStateChanges, editScopeGroupIdRef, selectedShapeIdsRef, shapesRef]
  )

  const unlockAll = useCallback(
    (opts?: { historyTxnId?: string }): string[] => {
      const ids = shapesRef.current
        .filter((shape) => shape.locked === true)
        .map((shape) => shape.id)

      return commitLockStateChanges(ids, false, 'unlock-all', opts?.historyTxnId)
    },
    [commitLockStateChanges, shapesRef]
  )

  const commitFreezeStateChanges = useCallback(
    (
      ids: string[],
      frozen: boolean,
      historyPrefix: 'freeze' | 'unfreeze' | 'unfreeze-all',
      historyTxnIdOverride?: string
    ): string[] => {
      const beforeList = shapesRef.current
      const beforeById = new Map(beforeList.map((shape) => [shape.id, shape]))
      const idsToChange = ids.filter((id) => {
        const shape = beforeById.get(id)

        if (!shape) return false
        if (shape.frozen === frozen) return false
        // Mutual exclusivity: a locked shape cannot be frozen.
        if (frozen && shape.locked === true) return false
        return true
      })

      if (idsToChange.length === 0) return []

      const idSet = new Set(idsToChange)
      const historyTxnId =
        historyTxnIdOverride ?? `${historyPrefix}:${idsToChange.slice().sort().join('|')}`
      const nextShapes = beforeList.map((shape) =>
        idSet.has(shape.id) ? { ...shape, frozen } : shape
      )

      shapesRef.current = nextShapes
      setShapes(nextShapes)
      onUserEdit?.(idsToChange)

      const nextById = new Map(nextShapes.map((shape) => [shape.id, shape]))

      for (const id of idsToChange) {
        const before = beforeById.get(id)
        const after = nextById.get(id)

        if (!before || !after) continue
        onShapeCommitted?.(after, 'update', {
          before: { frozen: before.frozen === true },
          after: { frozen },
          historyTxnId,
        })
      }

      return idsToChange
    },
    [onShapeCommitted, onUserEdit, setShapes, shapesRef]
  )

  const freezeSelected = useCallback(
    (opts?: { historyTxnId?: string }): string[] => {
      const ids = normalizeSelectableIdsForScope(
        selectedShapeIdsRef.current,
        shapesRef.current,
        editScopeGroupIdRef.current
      )

      return commitFreezeStateChanges(ids, true, 'freeze', opts?.historyTxnId)
    },
    [commitFreezeStateChanges, editScopeGroupIdRef, selectedShapeIdsRef, shapesRef]
  )

  const unfreezeSelected = useCallback(
    (opts?: { historyTxnId?: string }): string[] => {
      const ids = normalizeSelectableIdsForScope(
        selectedShapeIdsRef.current,
        shapesRef.current,
        editScopeGroupIdRef.current
      )

      return commitFreezeStateChanges(ids, false, 'unfreeze', opts?.historyTxnId)
    },
    [commitFreezeStateChanges, editScopeGroupIdRef, selectedShapeIdsRef, shapesRef]
  )

  const unfreezeAll = useCallback(
    (opts?: { historyTxnId?: string }): string[] => {
      const ids = shapesRef.current
        .filter((shape) => shape.frozen === true)
        .map((shape) => shape.id)

      return commitFreezeStateChanges(ids, false, 'unfreeze-all', opts?.historyTxnId)
    },
    [commitFreezeStateChanges, shapesRef]
  )

  const commitIgnoreTextWrapStateChanges = useCallback(
    (ids: string[], ignoreTextWrap: boolean, historyTxnIdOverride?: string): string[] => {
      const beforeList = shapesRef.current
      const beforeById = new Map(beforeList.map((shape) => [shape.id, shape]))
      const idsToChange = ids.filter((id) => {
        const shape = beforeById.get(id)

        return shape != null && shape.ignoreTextWrap !== ignoreTextWrap
      })

      if (idsToChange.length === 0) return []

      const historyTxnId =
        historyTxnIdOverride ?? `ignore-text-wrap:${idsToChange.slice().sort().join('|')}`
      const idsToChangeSet = new Set(idsToChange)
      const nextShapes = beforeList.map((shape) =>
        idsToChangeSet.has(shape.id) ? { ...shape, ignoreTextWrap } : shape
      )

      shapesRef.current = nextShapes
      setShapes(nextShapes)
      onUserEdit?.(idsToChange)

      const nextById = new Map(nextShapes.map((shape) => [shape.id, shape]))

      for (const id of idsToChange) {
        const before = beforeById.get(id)
        const after = nextById.get(id)

        if (!before || !after) continue
        onShapeCommitted?.(after, 'update', {
          before: { ignoreTextWrap: before.ignoreTextWrap === true },
          after: { ignoreTextWrap },
          historyTxnId,
        })
      }

      return idsToChange
    },
    [onShapeCommitted, onUserEdit, setShapes, shapesRef]
  )

  const setIgnoreTextWrapSelected = useCallback(
    (checked: boolean, opts?: { historyTxnId?: string }): string[] => {
      const ids = normalizeSelectableIdsForScope(
        selectedShapeIdsRef.current,
        shapesRef.current,
        editScopeGroupIdRef.current
      )

      return commitIgnoreTextWrapStateChanges(ids, checked, opts?.historyTxnId)
    },
    [commitIgnoreTextWrapStateChanges, editScopeGroupIdRef, selectedShapeIdsRef, shapesRef]
  )

  return {
    commitLockStateChanges,
    lockSelected,
    unlockSelected,
    unlockAll,
    commitFreezeStateChanges,
    freezeSelected,
    unfreezeSelected,
    unfreezeAll,
    commitIgnoreTextWrapStateChanges,
    setIgnoreTextWrapSelected,
  }
}

export type ShapeLockFreezeApi = ReturnType<typeof useShapeLockFreeze>
